import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Input } from '@/components/ui/input';
import IconArrowUp from '@/assets/icons/icon-arrow-up-03.svg?react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-03.svg?react';

interface DurationPickerProps {
  /** Label text to display */
  text: string;
  /** Duration in seconds */
  value: number;
  /** Callback when duration changes (receives seconds) */
  onChange: (seconds: number) => void;
  /** Optional className */
  className?: string;
}

interface DurationFields {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * DurationPicker component for selecting duration in Days, Hours, Minutes, and Seconds
 * Works directly with duration in seconds
 * Validation: Min 1 second, Max 365 days
 */
export const DurationPicker: React.FC<DurationPickerProps> = ({
  text,
  value,
  onChange,
  className = '',
}) => {
  // Convert seconds to duration fields
  const secondsToFields = (totalSeconds: number): DurationFields => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));

    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    return { days, hours, minutes, seconds };
  };

  // Convert duration fields to total seconds
  const fieldsToSeconds = (fields: DurationFields): number => {
    return fields.days * 86400 + fields.hours * 3600 + fields.minutes * 60 + fields.seconds;
  };

  const [fields, setFields] = useState<DurationFields>(() => secondsToFields(value));
  const [focusedField, setFocusedField] = useState<keyof DurationFields | null>(null);
  const [inputValues, setInputValues] = useState<Record<keyof DurationFields, string>>({
    days: fields.days.toString(),
    hours: fields.hours.toString(),
    minutes: fields.minutes.toString(),
    seconds: fields.seconds.toString(),
  });

  const isInternalUpdate = useRef(false);
  const lastValueRef = useRef(value);

  const inputRefs = useRef<Record<keyof DurationFields, HTMLInputElement | null>>({
    days: null,
    hours: null,
    minutes: null,
    seconds: null,
  });

  // Update fields when value changes externally (not from our own updates)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      lastValueRef.current = value;
      return;
    }

    // Only update if value actually changed
    if (lastValueRef.current !== value) {
      lastValueRef.current = value;
      const newFields = secondsToFields(value);
      setFields(newFields);
      setInputValues({
        days: newFields.days.toString(),
        hours: newFields.hours.toString(),
        minutes: newFields.minutes.toString(),
        seconds: newFields.seconds.toString(),
      });
    }
  }, [value]);

  const handleFieldChange = (field: keyof DurationFields, newValue: string) => {
    // Update input value for display
    setInputValues((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleFocus = (field: keyof DurationFields) => {
    setFocusedField(field);
  };

  const handleBlur = (field: keyof DurationFields) => {
    setFocusedField(null);

    // Parse and validate the number
    const num = parseInt(inputValues[field], 10);
    const parsedNum = isNaN(num) || num < 0 ? 0 : num;

    // Apply field-specific max limits
    const maxValues = {
      days: 365,
      hours: 23,
      minutes: 59,
      seconds: 59,
    };

    const clampedValue = Math.min(parsedNum, maxValues[field]);
    const newFields = { ...fields, [field]: clampedValue };

    // Calculate total seconds
    const totalSeconds =
      newFields.days * 86400 + newFields.hours * 3600 + newFields.minutes * 60 + newFields.seconds;

    // Validate: max 365 days
    const MAX_SECONDS = 365 * 86400;
    if (totalSeconds > MAX_SECONDS) {
      // Revert to previous value if exceeds max
      setInputValues((prev) => ({ ...prev, [field]: fields[field].toString() }));
      return;
    }

    // Validate: min 1 second (but allow intermediate 0 values during editing)
    // The validation toast will be shown on mode change or when trying to preview

    setFields(newFields);
    setInputValues((prev) => ({ ...prev, [field]: clampedValue.toString() }));
    isInternalUpdate.current = true;
    onChange(fieldsToSeconds(newFields));
  };

  const handleIncrement = (field: keyof DurationFields) => {
    const currentValue = fields[field];
    const maxValues = {
      days: 365,
      hours: 23,
      minutes: 59,
      seconds: 59,
    };

    if (currentValue >= maxValues[field]) return;

    const newFields = { ...fields, [field]: currentValue + 1 };
    const totalSeconds =
      newFields.days * 86400 + newFields.hours * 3600 + newFields.minutes * 60 + newFields.seconds;
    const MAX_SECONDS = 365 * 86400;

    if (totalSeconds > MAX_SECONDS) return;

    setFields(newFields);
    setInputValues((prev) => ({ ...prev, [field]: (currentValue + 1).toString() }));
    isInternalUpdate.current = true;
    onChange(fieldsToSeconds(newFields));
  };

  const handleDecrement = (field: keyof DurationFields) => {
    const currentValue = fields[field];
    if (currentValue <= 0) return;

    const newFields = { ...fields, [field]: currentValue - 1 };
    setFields(newFields);
    setInputValues((prev) => ({ ...prev, [field]: (currentValue - 1).toString() }));
    isInternalUpdate.current = true;
    onChange(fieldsToSeconds(newFields));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: keyof DurationFields) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      handleIncrement(field);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      handleDecrement(field);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const renderField = (field: keyof DurationFields, label: string) => {
    const isFocused = focusedField === field;
    const displayValue = isFocused ? inputValues[field] : fields[field].toString().padStart(2, '0');

    return (
      <div className="flex flex-col items-center gap-1">
        <div className="relative inline-flex items-center">
          <Input
            ref={(el) => {
              inputRefs.current[field] = el;
            }}
            className="h-8 min-h-8 w-[42px] pr-4 pl-2 text-center text-sm"
            type="text"
            inputMode="numeric"
            value={displayValue}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            onFocus={() => handleFocus(field)}
            onBlur={() => handleBlur(field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
            placeholder="00"
            maxLength={3}
          />

          <div className="pointer-events-none absolute top-1/2 right-0.5 flex -translate-y-1/2 flex-col">
            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700"
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleIncrement(field);
              }}
            >
              <IconArrowUp className="h-3 w-3" />
            </button>

            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700"
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleDecrement(field);
              }}
            >
              <IconArrowDown className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="text-xs text-neutral-400">{label}</div>
      </div>
    );
  };

  return (
    <div className={clsx('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-neutral-900">{text}</div>
      </div>
      <div className="flex items-center justify-between gap-1.5">
        {renderField('days', 'Days')}
        {renderField('hours', 'Hours')}
        {renderField('minutes', 'Mins')}
        {renderField('seconds', 'Secs')}
      </div>
    </div>
  );
};
